/* Copyright (c) 2020-present CloudIO Inc */

import { DBRow, IoHowTos } from '@cloudio-saas/datasource-types';
import {
  Box,
  IconButton,
  InputBase,
  Paper,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { getUrl } from 'config/config';
import Icon, { parseIconText } from 'icons/Icon';
import { useOrgNameAndLogo } from 'layout/Header/OrgLogo';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getURLQueryStringToPost } from 'utils/url';
import WaveDots from 'utils/WaveDots';
import TimeAgo from 'react-timeago';
import NoDataFound from 'utils/NoDataFound';
import { atom, useStateXAtom } from '@cloudio-saas/statex';
import { Platform } from 'componentregistry';
import { isSameHour, parseISO } from 'date-fns';
import { formatSeconds } from 'utils/dateUtils';
import { usePlatform } from 'platform/PlatformHooks';
import {
  isAdministrator,
  isDeveloper,
  isViewOnlyDeveloper,
} from 'root/useUser';

const bgColor = 'rgb(248 250 252)';

const howTosAtom = atom<DBRow<IoHowTos>[]>({
  path: ['howTos', ':appUid', 'data'],
  defaultValue: [],
});

const howToTagsAtom = atom<string[]>({
  path: ['howTos', ':appUid', 'tags'],
  defaultValue: [],
});

export function useHowTos(appUid: string): {
  howTos: DBRow<IoHowTos>[];
  loading: boolean;
  tags: string[];
  deleteHowTo: (record: DBRow<IoHowTos>) => Promise<void>;
} {
  const [howTos, setHowTos] = useStateXAtom(howTosAtom, { params: { appUid } });
  const [tags, setTags] = useStateXAtom(howToTagsAtom, { params: { appUid } });
  const [loading, setLoading] = useState(howTos.length === 0);
  const platform = usePlatform();

  const query = useCallback(async () => {
    try {
      const response = await fetch(
        `${getUrl()}public?${getURLQueryStringToPost()}`,
        {
          method: 'POST',
          headers: {
            'X-Application': appUid,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            a: {
              ds: 'IoHowTos',
              query: {
                limit: 2000,
                sort: { creationDate: -1 },
              },
            },
          }),
        },
      );
      const json = await response.json();
      if (json.status === 'OK') {
        const data = json.data.a.data as DBRow<IoHowTos>[];
        setTags(
          data
            .flatMap((howTo) => howTo.md?.tags || [])
            .reduce((array, tag) => {
              if (!array.includes(tag)) {
                array.push(tag);
              }
              return array;
            }, [] as string[])
            .sort(),
        );
        setHowTos(data);
      }
    } catch (e) {
      if (console) console.error(e);
    } finally {
      setLoading(false);
    }
  }, [appUid]);

  async function deleteHowTo(record: DBRow<IoHowTos>) {
    try {
      const response = await fetch(
        `${getUrl()}public?${getURLQueryStringToPost()}`,
        {
          method: 'POST',
          headers: {
            'X-Application': appUid,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            a: {
              ds: 'IoHowTos',
              data: [
                {
                  ...record,
                  _rs: 'D',
                },
              ],
            },
          }),
        },
      );
      const json = await response.json();
      if (json.status === 'OK') {
        await query();
        platform.showSuccess(
          `HowTo deleted Successfully! [[${record['howToTitle']}]]`,
        );
      }
    } catch (e) {
      if (console) console.error(e);
    }
  }

  useEffect(() => {
    if (howTos.length) {
      setLoading(false);
      return;
    }
    query();
  }, [appUid, howTos.length, query]);
  return { howTos, tags, loading, deleteHowTo };
}

export default function HowToHome({
  platform,
  internal,
  onClick,
}: {
  platform: Platform;
  internal?: boolean;
  onClick?: (howTo: DBRow<IoHowTos>) => void;
}) {
  const [{ logoImage, orgName, pending }] = useOrgNameAndLogo();
  const [searchParams] = useSearchParams();
  const params = useParams() as {
    appUid: string;
  };
  const tag = searchParams.get('tag');
  const [search, setSearch] = useState('');
  const { appUid } = params;
  const { howTos: _howTos, tags, loading, deleteHowTo } = useHowTos(appUid);
  let howTos = tag
    ? _howTos.filter((howTo) => howTo.md?.tags?.includes(tag))
    : _howTos;

  if (search) {
    const _lcSearch = search.toLowerCase();
    howTos = howTos.filter((howTo) => {
      return howTo.howToTitle.toLowerCase().includes(_lcSearch);
    });
  }

  const theme = useTheme();

  if (loading) {
    window.document.title = 'Loading...';
    return (
      <Box sx={{ display: 'flex', flex: 1, height: '100vh' }}>
        <WaveDots active />
      </Box>
    );
  }

  window.document.title = 'How Tos';

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        backgroundColor: 'rgb(248 250 252)',
      }}
    >
      {internal ? null : (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            zIndex: 20,
            p: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{ height: '3rem', cursor: 'pointer' }}
            onClick={() => {
              window.location.href = `#/${appUid}`;
            }}
          >
            {pending ? (
              <WaveDots active />
            ) : (
              <img
                style={{ maxHeight: '100%', maxWidth: '100%' }}
                src={logoImage}
                alt={orgName}
              />
            )}
          </Box>
        </Box>
      )}
      <Box
        sx={{ display: 'flex', flex: 1, mt: '90px', flexDirection: 'column' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              boxShadow:
                '0 0 #0000,0 0 #0000, 0 10px 15px -3px rgba(15,23,42,.1),0 4px 6px -4px rgba(15,23,42,.1)',
              borderRadius: '9999px',
              width: '88px',
              height: '88px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundImage: `linear-gradient(to bottom,${theme.palette.primary.main},#fff)`,
            }}
          >
            <Box
              sx={{
                bgcolor: 'background.paper',
                width: '4rem',
                height: '4rem',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon
                width={32}
                height={32}
                iconName="question"
                iconType="solid"
                color={theme.palette.primary.main}
              />
            </Box>
          </Box>
          <Typography variant="h4">How Tos</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            {howTos?.length} How Tos
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              flexWrap: 'wrap',
              maxWidth: 720,
            }}
          >
            <Tags appUid={appUid} tags={tags} selected={tag} home />
          </Box>
          <Paper
            component="form"
            sx={{
              p: '2px 16px',
              display: 'flex',
              alignItems: 'center',
              minWidth: 300,
              maxWidth: 400,
              borderRadius: '20px',
              flex: 1,
            }}
            variant="outlined"
          >
            <InputBase
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              sx={{ flex: 1 }}
              placeholder="Filter..."
            />
          </Paper>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            mb: 8,
            p: 2,
            flex: 1,
            minHeight: '400px',
            position: 'relative',
          }}
        >
          {howTos.length ? (
            howTos.map((howTo, i) => (
              <HowToEntry
                key={howTo.howToUid}
                appUid={appUid}
                howTo={howTo}
                index={i + 1}
                theme={theme}
                onClick={onClick}
                home
                deleteHowTo={deleteHowTo}
              />
            ))
          ) : (
            <NoDataFound
              message={
                <>
                  <Typography variant="body1">
                    No How Tos found for this app!
                  </Typography>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      window.location.href = `#/${appUid}`;
                    }}
                  >
                    <Icon iconName="home" color="primary" />
                  </IconButton>
                </>
              }
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function HowToEntry({
  appUid,
  howTo,
  index,
  theme,
  onClick,
  sx = {},
  home,
  deleteHowTo,
}: {
  appUid: string;
  howTo: DBRow<IoHowTos>;
  index: number;
  theme: Theme;
  onClick?: (_howTo: DBRow<IoHowTos>) => void;
  sx?: SxProps<Theme>;
  home?: boolean;
  deleteHowTo?: (record: DBRow<IoHowTos>) => Promise<void>;
}) {
  const { iconName = 'desktop', iconType } = parseIconText(howTo.icon) ?? {};
  const color = howTo.iconColor ?? theme.palette.primary.main;
  const platform = usePlatform();
  const developer = isDeveloper(platform, appUid);
  const admin = isAdministrator(platform, appUid);
  const viewOnly = isViewOnlyDeveloper(platform, appUid);

  return (
    <Box
      className="how-to-entry"
      sx={{
        maxWidth: '720px',
        width: '100%',
        m: 2,
        p: 2,
        borderRadius: '20px',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        boxShadow:
          '0 0 #0000,0 0 #0000,0 1px 3px 0 rgba(15,23,42,.1),0 1px 2px -1px rgba(15,23,42,.1)',
        ...sx,
      }}
      onClick={() => {
        if (onClick) {
          onClick(howTo);
        } else {
          window.location.href = `#/how-to/${appUid}/${howTo.howToUid}`;
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            aria-label={`Step ${index}`}
            sx={{
              bgcolor: 'rgb(238 242 255)',
              borderRadius: '9999px',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            <Box
              sx={{
                boxShadow:
                  '0 0 #0000,0 0 #0000, 0 10px 15px -3px rgba(15,23,42,.1),0 4px 6px -4px rgba(15,23,42,.1)',
                borderRadius: '9999px',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundImage: `linear-gradient(to bottom,${color},#fff)`,
              }}
            >
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  width: '32px',
                  height: '32px',
                  borderRadius: '9999px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon
                  width={16}
                  height={16}
                  iconName={iconName}
                  iconType={iconType}
                  color={color}
                />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6">{howTo.howToTitle ?? ''}</Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: theme.palette.text.secondary,
                flexWrap: 'wrap',
              }}
            >
              {howTo.totalSteps + 1} Steps
              <span> | </span>
              {formatSeconds(howTo.totalSeconds)}
              <span> | </span>
              <TimeAgo
                date={howTo.lastUpdateDate ?? ''}
                data-tip={
                  isSameHour(
                    parseISO(howTo.lastUpdateDate),
                    parseISO(howTo.creationDate),
                  )
                    ? 'Created On'
                    : 'Updated On'
                }
              />
              {howTo.md?.tags ? (
                <>
                  <span> | </span>
                  <Tags appUid={appUid} tags={howTo.md.tags} home={home} />
                </>
              ) : null}
            </Box>
          </Box>
        </Box>
        {deleteHowTo && (developer || admin) && !viewOnly ? (
          <Box>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                deleteHowTo(howTo);
              }}
              data-tip="Delete this HowTo"
            >
              <Icon iconName="trash-alt" color="error" />
            </IconButton>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export function Tags({
  tags,
  appUid,
  selected,
  home,
}: {
  tags: string[];
  appUid: string;
  selected?: string | null;
  home?: boolean;
}): React.ReactElement {
  const [, setSearchParams] = useSearchParams();

  return (
    <>
      {tags.map((tag) => (
        <Box
          key={tag}
          sx={{
            bgcolor: selected === tag ? 'primary.main' : bgColor,
            border: '1px solid',
            borderColor: selected === tag ? 'primary.dark' : 'divider',
            borderRadius: '16px',
            color: selected === tag ? 'primary.contrastText' : 'text.primary',
            cursor: 'pointer',
            px: 1,
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (home) {
              const params = new URLSearchParams(window.location.search);
              if (selected === tag) {
                params.delete('tag');
              } else {
                params.set('tag', tag);
              }
              setSearchParams(params);
            } else {
              window.location.href = `#/how-to/${appUid}?tag=${tag}`;
            }
          }}
        >
          {tag}
        </Box>
      ))}
    </>
  );
}
